<template>
  <div class="user-card" @click="emitClick">
    <div class="card-image-container">
      <img :src="user.profileImage || getAvatarUrl(user.name)" :alt="user.name" class="card-image" />

    </div>
    
    <div class="card-content">
      <div class="user-identity">
        <h3 class="user-name">{{ user.name }}</h3>
        <span v-if="user.isActive" class="status-dot active"></span>
      </div>
      
      <p class="user-role">{{ user.bot }}</p>
      
      <div class="user-stats">
        <div class="stat-item">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
             <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          <span>{{ user.discussions }}</span>
        </div>
        <div class="stat-item">
           <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
           </svg>
           <span>{{ formatTimeAgo(user.lastActivity) }}</span>
        </div>
      </div>

      <div class="ai-status-row" @click.stop>
        <div class="ai-status-container" :class="{ 'active': user.talkToAi !== false }">
          <span class="ai-label">Réponse IA</span>
          <label class="toggle-switch small">
            <input 
              type="checkbox" 
              :checked="user.talkToAi !== false" 
              @change="handleAiToggle($event)"
            >
            <span class="slider round"></span>
          </label>
        </div>
      </div>
      
      <div class="card-actions">
        <button class="action-btn primary" @click.stop="$emit('open-conversation', user)">
            Voir conversation
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';

export default {
  name: 'UserCard',
  props: {
    user: {
      type: Object,
      required: true
    }
  },
  emits: ['open-conversation', 'click', 'toggle-ai-status'],
  setup(props, { emit }) {
    
    // Helper pour générer l'avatar si manquant
    const getAvatarUrl = (name) => {
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&color=fff&size=200`;
    };

    // Helper pour le temps (similaire à AllUsersPage)
    const formatTimeAgo = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);
      
      if (diffInSeconds < 60) return 'À l\'instant';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} h`;
      return `${Math.floor(diffInSeconds / 86400)} j`;
    };

    const emitClick = () => {
        emit('click', props.user);
    };

    const handleAiToggle = (event) => {
      emit('toggle-ai-status', props.user, event.target.checked);
    };

    return {
      getAvatarUrl,
      formatTimeAgo,
      emitClick,
      handleAiToggle
    };
  }
}
</script>

<style scoped>
.user-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);
  border: 1px solid rgba(0,0,0,0.03);
  display: flex;
  flex-direction: column;
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
  margin-bottom: 16px;
  position: relative;
}

.user-card:active {
  transform: scale(0.98);
}

.card-image-container {
  width: 100%;
  height: 200px; /* Grande image comme sur le design */
  background: #F3F4F6;
  position: relative;
  overflow: hidden;
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.user-card:hover .card-image {
  transform: scale(1.05);
}

.verified-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1ABB34; /* WhatsApp Greenish */
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: none; /* Hide original badge container styles if mixed */
}

.verified-badge svg {
  width: 20px;
  height: 20px;
}

/* AI Status Row */
.ai-status-row {
  width: 100%;
  margin-bottom: 20px;
}

.ai-status-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #F9FAFB;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
  transition: all 0.3s ease;
}

.ai-status-container.active {
  background: #ECFDF5;
  border-color: #A7F3D0;
}

.ai-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.ai-status-container.active .ai-label {
  color: #059669;
}

/* Toggle Switch Styles */
/* Toggle Switch Styles - Premium iOS Style */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #E5E7EB; /* Gray-200 */
  transition: all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
  border: 1px solid #D1D5DB; /* Gray-300 border for definition */
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  transition: all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
}

input:checked + .slider {
  background-color: #10B981; /* Emerald-500 */
  border-color: #10B981;
}

input:focus + .slider {
  box-shadow: 0 0 0 2px #E5E7EB, 0 0 0 4px #10B981;
}

input:checked + .slider:before {
  transform: translateX(20px);
}

.slider.round {
  border-radius: 24px;
}

.slider.round:before {
  border-radius: 50%;
}


.card-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.user-identity {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
}

.user-name {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #D1D5DB;
}

.status-dot.active {
    background-color: #1ABB34;
    box-shadow: 0 0 0 2px rgba(26, 187, 52, 0.2);
}

.user-role {
  font-size: 14px;
  color: #6B7280;
  margin: 0 0 16px 0;
  font-weight: 500;
}

.user-stats {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #4B5563;
  font-size: 14px;
  font-weight: 500;
  background: #F9FAFB;
  padding: 4px 10px;
  border-radius: 20px;
}

.card-actions {
  width: 100%;
}

.action-btn {
  width: 100%;
  padding: 10px 16px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
}

.action-btn.primary {
  background: #F3F4F6;
  color: #111827;
}

.action-btn.primary:hover {
  background: #E5E7EB;
}
</style>
